/**
 * Single aws-amplify entry point for auth-shell.
 * Import Amplify/Auth/Analytics only from this module so Vite bundles one singleton.
 */
import { Amplify, Auth, Analytics } from "aws-amplify";
import { Credentials } from "@aws-amplify/core";
import { SharedConfig } from "@fmc-digital/core-utils";

import type { RuntimeConfig } from "@/config/auth.config";

import { AUTH_APPLICATION } from "./constants";

export { Analytics } from "aws-amplify";

let amplifyAnalyticsReady = false;

export function isAmplifyAnalyticsReady(): boolean {
  return amplifyAnalyticsReady;
}

/** Reset ready state (tests only). */
export function resetAmplifyAnalyticsReadyForTesting(): void {
  amplifyAnalyticsReady = false;
}

/**
 * Configures Pinpoint analytics and warms Cognito guest credentials for event delivery.
 */
export async function configureAmplifyAnalytics(
  runtimeConfig: RuntimeConfig,
): Promise<void> {
  const {
    awsProjectRegion,
    awsCognitoIdentityPoolId,
    awsCognitoRegion,
    awsMobileAnalyticsAppId,
    awsMobileAnalyticsAppRegion,
  } = runtimeConfig;

  amplifyAnalyticsReady = false;

  if (
    !awsProjectRegion ||
    !awsCognitoIdentityPoolId ||
    !awsCognitoRegion ||
    !awsMobileAnalyticsAppId ||
    !awsMobileAnalyticsAppRegion
  ) {
    console.warn(
      "[App] Amplify analytics config missing; skipping Amplify initialization",
    );
    return;
  }

  try {
    console.log("[App] Initializing Amplify analytics");

    SharedConfig.set("amplify", { application: AUTH_APPLICATION });

    Amplify.configure({
      aws_project_region: awsProjectRegion,
      aws_cognito_identity_pool_id: awsCognitoIdentityPoolId,
      aws_cognito_region: awsCognitoRegion,
      aws_mobile_analytics_app_id: awsMobileAnalyticsAppId,
      aws_mobile_analytics_app_region: awsMobileAnalyticsAppRegion,
    });

    // Ensure AWSPinpoint provider receives appId/region on the same Analytics instance used for record().
    Analytics.configure({
      AWSPinpoint: {
        appId: awsMobileAnalyticsAppId,
        region: awsMobileAnalyticsAppRegion,
        flushInterval: 5_000,
        flushSize: 1,
      },
    });

    Auth.configure({
      mandatorySignIn: false,
      identityPoolId: awsCognitoIdentityPoolId,
      region: awsCognitoRegion,
    });

    const credentials = await Credentials.get();
    if (!credentials) {
      console.warn(
        "[App] Amplify credentials unavailable; Pinpoint events may fail until credentials load",
      );
      return;
    }

    amplifyAnalyticsReady = true;
    console.log("[App] Amplify initialization completed");
  } catch (error) {
    console.error("[App] Failed to initialize Amplify:", error);
    console.error("[App] Amplify initialization error details:", {
      awsProjectRegion,
      awsCognitoIdentityPoolId,
      awsCognitoRegion,
      awsMobileAnalyticsAppId,
      awsMobileAnalyticsAppRegion,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
